import type { TFunction } from 'i18next'

/**
 * What the buyer is told when Coinbase's page refuses the payment.
 *
 * Coinbase-only: these codes come from their headless onramp and mean nothing
 * to another provider. Their `errorMessage` is not used — it is written against
 * their page, and their docs word several codes as notes to the integrator.
 *
 * `undefined` for a code with no copy yet, which the screen answers with its
 * own generic line. One literal key per branch, as `t()` requires
 * (`.ast-grep/rules/i18n-t-string-literal.yml`).
 */
export function coinbaseFailureCopy(
  code: string,
  t: TFunction,
): string | undefined {
  switch (code) {
    case 'ERROR_CODE_GUEST_APPLE_PAY_NOT_SETUP':
      return t('fiat.applePayNotSetUp')
    case 'ERROR_CODE_GUEST_APPLE_PAY_NOT_SUPPORTED':
      return t('fiat.applePayUnsupported')
    case 'ERROR_CODE_GUEST_CARD_SOFT_DECLINED':
      return t('fiat.coinbaseCardSoftDeclined')
    case 'ERROR_CODE_GUEST_CARD_HARD_DECLINED':
      return t('fiat.coinbaseCardHardDeclined')
    case 'ERROR_CODE_GUEST_CARD_RISK_DECLINED':
      return t('fiat.coinbaseCardRiskDeclined')
    case 'ERROR_CODE_GUEST_INVALID_CARD':
      return t('fiat.coinbaseInvalidCard')
    case 'ERROR_CODE_GUEST_CARD_INSUFFICIENT_BALANCE':
      return t('fiat.coinbaseInsufficientBalance')
    case 'ERROR_CODE_GUEST_CARD_PREPAID_DECLINED':
      return t('fiat.coinbasePrepaidCard')
    // Two codes, one situation: the buyer is somewhere the rail cannot serve,
    // and nothing they do on this screen changes that.
    case 'ERROR_CODE_GUEST_REGION_MISMATCH':
    case 'ERROR_CODE_GUEST_REGION_FORBIDDEN':
      return t('fiat.coinbaseRegionUnsupported')
    case 'ERROR_CODE_GUEST_PERMISSION_DENIED':
      return t('fiat.coinbasePermissionDenied')
    case 'ERROR_CODE_GUEST_TRANSACTION_LIMIT':
      return t('fiat.coinbaseWeeklyLimit')
    // Coinbase names a figure for this one (15 as of writing). Left out of the
    // copy: it is theirs to change, and a stale number reads as a promise.
    case 'ERROR_CODE_GUEST_TRANSACTION_COUNT':
      return t('fiat.coinbaseLifetimeCount')
    case 'ERROR_CODE_GUEST_DAILY_TRANSACTION_COUNT':
      return t('fiat.coinbaseDailyCount')
    case 'ERROR_CODE_INVALID_BILLING_ZIP':
      return t('fiat.coinbaseBillingZip')
    case 'ERROR_CODE_INVALID_BILLING_ADDRESS':
      return t('fiat.coinbaseBillingAddress')
    case 'ERROR_CODE_INVALID_BILLING_NAME':
      return t('fiat.coinbaseBillingName')
    case 'ERROR_CODE_INIT':
      return t('fiat.coinbaseSessionExpired')
    case 'ERROR_CODE_NETWORK_NOT_TRADEABLE':
      return t('fiat.coinbaseNetworkUnsupported')
    case 'ASSET_NOT_TRADABLE':
      return t('fiat.coinbaseAssetUnsupported')
    // Nothing the buyer did, and nothing they can fix — INVALID_REQUEST is a
    // fault in the order we asked Coinbase for. All three say the same thing.
    case 'ERROR_CODE_INTERNAL':
    case 'ERROR_CODE_INVALID_REQUEST':
    case 'ERROR_CODE_GUEST_TRANSACTION_TRANSACTION_FAILED':
      return t('fiat.coinbaseProviderError')
    // The money answers are Coinbase's own, stated per code in their docs, and
    // they are the first thing a buyer wants after a failure.
    case 'ERROR_CODE_GUEST_TRANSACTION_BUY_FAILED':
      return t('fiat.coinbaseBuyFailed')
    case 'ERROR_CODE_GUEST_TRANSACTION_SEND_FAILED':
      return t('fiat.coinbaseSendFailed')
    case 'ERROR_CODE_GUEST_TRANSACTION_AVS_VALIDATION_FAILED':
      return t('fiat.coinbaseAvsFailed')
    // `ERROR_CODE_GUEST_GOOGLE_PAY_NOT_SUPPORTED` is deliberately absent: the
    // adapter claims Apple Pay button surfaces only, so that page never loads
    // here and never posts.
    default:
      return undefined
  }
}
